/**
 * GateFallback — what a route guard renders while it sends you somewhere else.
 *
 * Guarded screens used to `return null` while `useVerificationGate` /
 * `useAmlGate` dispatched their redirect. The comment said it "prevents the
 * form from flashing before navigation", but `null` inside a mounted route is
 * not nothing — it is a full-screen blank, painted in the window background
 * (white) regardless of theme. Caught on a tablet during the release E2E pass:
 * five hosting flows screenshotted a completely white page, because the
 * redirect had not landed yet and there was nothing to look at in the meantime.
 *
 * A blank page is worse than the flash it was avoiding: it reads as a crash,
 * and in dark mode it is a white strobe. STANDARDS §8 — no visible intermediate
 * states — means the in-between has to look like the app, not like a failure.
 *
 * This renders the app's own background plus a platform-reliable progress
 * indicator and a visible status label, so a slow redirect never reads as a
 * blank or crashed screen.
 */
import { ActivityIndicator, Text, View } from 'react-native'
import { useTheme } from '../../lib/hooks/use-theme'
import { SPACING, TEXT_STYLES, useSemanticColors } from '../../lib/theme/tokens'

interface GateFallbackProps {
  /**
   * Announced to screen readers. Pass the reason the guard fired so the
   * transition is not silent for anyone who cannot see the spinner.
   */
  accessibilityLabel: string
  testID?: string
}

export function GateFallback({
  accessibilityLabel,
  testID = 'gate.redirecting',
}: GateFallbackProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background,
      }}
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <ActivityIndicator size="small" color={colors.primary} />
      <Text
        style={{
          ...TEXT_STYLES.bodySm,
          marginTop: SPACING.sm,
          color: colors.textSecondary,
          textAlign: 'center',
        }}
        accessibilityElementsHidden
      >
        {accessibilityLabel}
      </Text>
    </View>
  )
}
